import React, { useEffect, useMemo, useState } from "react";
import { useDataLayer } from "../../../../Contexts/DataLayer.context"; // adjust path if needed

/** ---------- Helpers ---------- **/

// "LAST, First" safely
const buildLastFirst = (last, first) => {
  const L = (last || "").trim();
  const F = (first || "").trim();
  return L && F ? `${L}, ${F}` : (L || F || "");
};

// SQL DATETIME -> input[type=datetime-local]
const toLocalInput = (dt) => {
  if (!dt) return "";
  const str = typeof dt === "string" ? dt : String(dt);
  const d = new Date(str.replace(" ", "T"));
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const min = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
};

// input[type=datetime-local] -> SQL DATETIME
const toSqlDatetime = (localVal) => {
  if (!localVal) return null;
  const d = new Date(localVal);
  if (isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, "0");
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:00`;
};

// Empty form shape
const emptyForm = {
  ID: "-1",
  Registration_No: "",
  LastFirstName: "",
  First: "",
  LAST: "",
  Company: "",
  Address1: "",
  Address2: "",
  City: "",
  Prov_State: "",
  PostalCode: "",
  Phone: "",
  Phone2: "",
  PhCell: "",
  Fax: "",
  Email: "",
  email2: "",
  Notes: "",
};

// Map agent row -> form
const fromAgent = (a = {}) => ({
  ID: a.ID ?? "",
  Registration_No: a.Registration_No ?? "",
  LAST: a.LAST ?? "",
  First: a.First ?? a.FIRST ?? "",
  LastFirstName: buildLastFirst(a.LAST, a.First ?? a.FIRST),
  Company: a.Company ?? "",
  Address1: a.Address1 ?? "",
  Address2: a.Address2 ?? "",
  City: a.City ?? "",
  Prov_State: a.Prov_State ?? "",
  PostalCode: a.PostalCode ?? "",
  Phone: a.Phone ?? "",
  Phone2: a.Phone2 ?? "",
  PhCell: a.PhCell ?? "",
  Fax: a.Fax ?? "",
  Email: a.Email ?? "",
  email2: a.email2 ?? "",
  Notes: a.Notes ?? "",
});

// Map form -> payload for save (recompute LastFirstName on save)
const toAgentPayload = (form) => {
  const last = (form.LAST || "").trim();
  const first = (form.First || "").trim();
  const lastFirstName = buildLastFirst(last, first);

  return {
    ID: String(form.ID ?? "").trim(),
    Registration_No: String(form.Registration_No ?? "").trim() || '',

    LastFirstName: lastFirstName,
    First: first,
    LAST: last,

    Company: (form.Company || "").trim(),
    Address1: (form.Address1 || "").trim(),
    Address2: (form.Address2 || "").trim(),
    City: (form.City || "").trim(),
    Prov_State: (form.Prov_State || "").trim(),
    PostalCode: (form.PostalCode || "").trim(),

    Phone: (form.Phone || "").trim(),
    Phone2: (form.Phone2 || "").trim(),
    PhCell: (form.PhCell || "").trim(),
    Fax: (form.Fax || "").trim(),
    Email: (form.Email || "").trim(),
    email2: (form.email2 || "").trim(),
    Notes: (form.Notes || "").trim(),
  };
};

/** ---------- Component ---------- **/

const AgentEditor = ({}) => {

  const {agentArray, updateAgentArray } = useDataLayer();
  const [selectedId, setSelectedId] = useState("");
  const [form, setForm] = useState(emptyForm);       // draft
  const [original, setOriginal] = useState(emptyForm); // snapshot
  const [changed, setChanged] = useState(() => new Set()); // changed field keys
  const [saving, setSaving] = useState(false);

  // options list
  const options = useMemo(
    () =>
      agentArray
        .slice()
        .map((a) => ({
          id: String(a.ID ?? ""),
          label: a.LastFirstName || "(Unnamed)",
        }))
        .filter((o) => o.id),
    [agentArray]
  );

  // When an agent is picked (or initialAgentId provided), load & snapshot
  useEffect(() => {
    if (!selectedId) {
      setForm(emptyForm);
      setOriginal(emptyForm);
      setChanged(new Set());
      return;
    }
    const found = agentArray.find((a) => String(a.ID ?? "") === String(selectedId));
    const next = found ? fromAgent(found) : emptyForm;
    setForm(next);
    setOriginal(next);       // <-- save original snapshot
    setChanged(new Set());   // reset changed fields
  }, [selectedId, agentArray]);

  // Utility: mark a key as dirty or clean comparing draft vs original
  const updateChangedForKey = (nextForm, key, set = true) => {
    setChanged((prev) => {
      const s = new Set(prev);
      const cur = String(nextForm?.[key] ?? "");
      const orig = String(original?.[key] ?? "");
      if (cur !== orig) s.add(key);
      else s.delete(key);
      return s;
    });
  };

  // Field change handler
  const handleChange = (key, value) => {
    setForm((prev) => {
      const nf = { ...prev, [key]: value };

      // If user changed First or LAST, recompute LastFirstName and track as changed
      if (key === "First" || key === "LAST") {
        nf.LastFirstName = buildLastFirst(nf.LAST, nf.First);
      }

      // Update changed set for edited key
      updateChangedForKey(nf, key);

      // Also update changed state for derived LastFirstName when needed
      if (key === "First" || key === "LAST") {
        updateChangedForKey(nf, "LastFirstName");
      }

      return nf;
    });
  };

  const isDirty = changed.size > 0;
  const ENDPOINT = "https://www.fbxchange.com/ReactPHP/tomBox.php";
  

  // Red highlight class for changed fields
  const inputCls = (key, base = "form-control fs-7") =>
    changed.has(key) ? `${base} border border-2 border-danger` : base;

  // Save handler — computes payload, persists, updates DL, resets snapshots + changed
  const handleSave = async () => {
    if (!isDirty) return;
    // setSaving(true);
    try {
      const payload = toAgentPayload(form);
      const response = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ script: "updateAgent", payload: payload }),
      });
      const data = await response.json();
      if (!data?.ok) throw new Error(data?.error || "Save failed");

      // Update DataLayer
      const idx = agentArray.findIndex((a) => String(a.ID ?? "") === String(payload.ID ?? ""));

      const merged = { ...payload, ...data.saved, ID: payload.ID };
      const nextRow = {
        ...merged,
      };
      const nextArray =
        idx >= 0
          ? [...agentArray.slice(0, idx), { ...agentArray[idx], ...merged }, ...agentArray.slice(idx + 1)]
          : [...agentArray, merged];
      updateAgentArray(nextArray);

      // Refresh editor state from the saved row
      const nextForm = fromAgent(nextRow);
      setForm(nextForm);
      setOriginal(nextForm);
      setChanged(new Set()); // clear changes
      setSelectedId(payload.ID);
    } catch (e) {
      console.error(e);
      alert(e?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  // Start a brand-new agent
  const handleNew = () => {
    setSelectedId("");
    setForm(emptyForm);
    setOriginal(emptyForm);
    setChanged(new Set());
    setTimeout(() => document.getElementById("ag_LAST")?.focus(), 0);
  };

return (
    <div className="container-fluid">
        {/* Header */}
        <div className="row align-items-center g-2 mb-2">
            <div className="col text-center text-uppercase fw-semibold py-2 border rounded">
                Agent Information
            </div>
            <div className="col-auto d-grid ms-auto">
                <button className="btn btn-primary text-uppercase fs-7 disabled" onClick={handleNew}>
                    New Agent
                </button>
            </div>
        </div>

        {/* Picker */}
        <div className="row g-2 mb-2">
            <div className="col-48 col-lg-32">
                <select
                    className="form-select"
                    value={selectedId}
                    onChange={(e) => setSelectedId(e.target.value)}
                >
                    <option value="">— Select Existing Agent —</option>
                    {options.map((o) => (
                        <option key={o.id} value={o.id}>
                            {o.label}
                        </option>
                    ))}
                </select>
            </div>
            <div className="col-48 col-lg-16">
                <div className="form-text">
                    Pick to edit, or click <strong>New</strong> to create.
                </div>
            </div>
        </div>

        {/* Read-only LastFirst & ID */}
        <div className="row g-2 mb-2">
            

        {/* LAST / First */}
        <div className="row g-1 d-flex">
            <div className="col-48 col-md-12">
                <input
                    id="ag_LAST"
                    className={inputCls("LAST")}
                    value={form.LAST}
                    onChange={(e) => handleChange("LAST", e.target.value)}
                    placeholder="LAST"
                />
            </div>
            <div className="col-48 col-md-12">
                <input
                    className={inputCls("First")}
                    value={form.First}
                    onChange={(e) => handleChange("First", e.target.value)}
                    placeholder="First"
                />
            </div>
            <div className="col-48 col-md-18">
                <input
                    className={inputCls("LastFirstName")}
                    readOnly
                    value={form.LastFirstName}
                    placeholder="Last, First (computed)"
                    title="Computed from LAST and First"
                />
            </div>
            <div className="col-48 col-md-6">
                <input className="form-control" readOnly value={form.ID} placeholder="ID (read-only)" />
            </div>
        </div>
        </div>

        {/* Registration_No / Company */}
        <div className="row g-1 mb-1">
            <div className="col-48 col-md-16">
                <input
                    className={inputCls("Registration_No")}
                    value={form.Registration_No}
                    onChange={(e) => handleChange("Registration_No", e.target.value)}
                    placeholder="Registration_No"
                />
            </div>
            <div className="col-48 col-md-32">
                <input
                    className={inputCls("Company")}
                    value={form.Company}
                    onChange={(e) => handleChange("Company", e.target.value)}
                    placeholder="Company"
                />
            </div>
        </div>

        {/* Address1 / Address2 */}
        <div className="row g-2 mb-1">
            <div className="col-48">
                <input
                    className={inputCls("Address1")}
                    value={form.Address1}
                    onChange={(e) => handleChange("Address1", e.target.value)}
                    placeholder="Address1"
                />
            </div>
        </div>
        <div className="row g-1 mb-1">
            <div className="col-48">
                <input
                    className={inputCls("Address2")}
                    value={form.Address2}
                    onChange={(e) => handleChange("Address2", e.target.value)}
                    placeholder="Address2"
                />
            </div>
        </div>

    {/* City / Prov_State / PostalCode */}
            <div className="row g-2 mb-1">
                <div className="col-48 col-md-16">
                    <input
                    className={inputCls("City")}
                    value={form.City}
                    onChange={(e) => handleChange("City", e.target.value)}
                    placeholder="City"
                    />
                </div>
                <div className="col-48 col-md-16">
                    <input
                    className={inputCls("Prov_State")}
                    value={form.Prov_State}
                    onChange={(e) => handleChange("Prov_State", e.target.value)}
                    placeholder="Prov/State"
                    />
                </div>
                <div className="col-48 col-md-16">
                    <input
                    className={inputCls("PostalCode")}
                    value={form.PostalCode}
                    onChange={(e) => handleChange("PostalCode", e.target.value)}
                    placeholder="Postal Code/Zip"
                    />
                </div>
            </div>

            {/* Phone / Phone2 / PhCell / Fax */}
        <div className="row g-2 mb-1">
            <div className="col-48 col-md-12">
                <input
                    className={inputCls("Phone")}
                    value={form.Phone}
                    onChange={(e) => handleChange("Phone", e.target.value)}
                    placeholder="Phone"
                />
            </div>
            <div className="col-48 col-md-12">
                <input
                    className={inputCls("Phone2")}
                    value={form.Phone2}
                    onChange={(e) => handleChange("Phone2", e.target.value)}
                    placeholder="Phone2"
                />
            </div>
            <div className="col-48 col-md-12">
                <input
                    className={inputCls("PhCell")}
                    value={form.PhCell}
                    onChange={(e) => handleChange("PhCell", e.target.value)}
                    placeholder="PhCell"
                />
            </div>
            <div className="col-48 col-md-12">
                <input
                    className={inputCls("Fax")}
                    value={form.Fax}
                    onChange={(e) => handleChange("Fax", e.target.value)}
                    placeholder="Fax"
                />
            </div>
        </div>

        {/* Email / email2 */}
        <div className="row g-2 mb-1">
            <div className="col-48 col-md-24">
                <input
                    className={inputCls("Email")}
                    value={form.Email}
                    onChange={(e) => handleChange("Email", e.target.value)}
                    placeholder="Email"
                />
            </div>
            <div className="col-48 col-md-24">
                <input
                    className={inputCls("email2")}
                    value={form.email2}
                    onChange={(e) => handleChange("email2", e.target.value)}
                    placeholder="email2"
                />
            </div>
        </div>

        {/* Notes */}
        <div className="row g-2 mb-2">
            <div className="col-48">
                <textarea
                    className={inputCls("Notes", "form-control fs-7")}
                    rows={2}
                    value={form.Notes}
                    onChange={(e) => handleChange("Notes", e.target.value)}
                    placeholder="Notes"
                />
            </div>
        </div>

        {/* Save at bottom — always shows, but disabled if not dirty */}
        <div className="row g-2">
            <div className="col-48 d-grid">
                <button
                    className="btn btn-sm btn-success text-white"
                    onClick={handleSave}
                    disabled={!isDirty || saving}
                    title={isDirty ? "Save changes" : "No changes to save"}
                >
                    {saving ? "Saving..." : "Save"}
                </button>
            </div>
        </div>
    </div>
);
};

export default AgentEditor;
